import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { VideoJSPlayer } from '../../components/VideoPlayer';
import { getLiveStream, createHlsSession } from '../../services/liveStreamApi/liveStreamApi';
import type { LiveStream } from '../../services/liveStreamApi/liveStreamApi';
import { BACKEND_URL } from '../../config';
import styles from './LiveStreamDetail.module.css';

const STATUS_LABELS: Record<LiveStream['status'], string> = {
  scheduled: '配信予定',
  live: 'LIVE',
  ended: '終了',
  error: 'エラー',
};

const HLS_POLL_INTERVAL_MS = 10_000;
const RETRY_INTERVAL_MS = 5_000;
const MAX_AUTO_RETRIES = 12; // 連続失敗の上限（最大 1 分間自動リトライ）
// 視聴セッション通算の復帰回数上限。連続失敗カウンタは再生成功でリセットされるため、
// 「短時間だけ再生成功 → 失効 → remount」を無限に繰り返すループを別途この上限で止める。
const MAX_SESSION_RECOVERIES = 20;

export default function LiveStreamDetail() {
  const { id } = useParams<{ id: string }>();
  const [stream, setStream] = useState<LiveStream | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  // playerKey を変えると VideoJSPlayer が再マウントされ HLS を再試行する
  const [playerKey, setPlayerKey] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);
  // 視聴セッション通算の復帰回数（再生成功でリセットしない。無限復帰ループ防止用）
  const sessionRecoveriesRef = useRef(0);
  const [sessionGaveUp, setSessionGaveUp] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStream = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getLiveStream(id)
      .then(setStream)
      .catch(() => setError('ライブ配信の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchStream();
  }, [fetchStream]);

  // 配信開始待ちとライブ中はステータスをポーリング（バックグラウンドタブでは休止）
  useEffect(() => {
    if (!id || (stream?.status !== 'scheduled' && stream?.status !== 'live')) return;
    const timer = setInterval(() => {
      if (document.hidden) return;
      getLiveStream(id)
        .then(setStream)
        .catch(() => undefined);
    }, HLS_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [id, stream?.status]);

  // HLS ロード失敗時: 一定間隔で自動リトライ。
  // 再生開始後でもトークン絶対上限(約60分)到達などで失効し得るため復帰を試みる
  // （remount → HLS URL 再取得 = createHlsSession 再認可）。
  // 依存を持たない安定コールバックにして、下の HLS 取得 effect の不要な再発火を防ぐ。
  const handlePlayerError = useCallback(() => {
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }

    // セッション通算の復帰上限に達したら、以後は自動復帰せず諦める（無限ループ防止）
    sessionRecoveriesRef.current += 1;
    if (sessionRecoveriesRef.current > MAX_SESSION_RECOVERIES) {
      setSessionGaveUp(true);
      setIsWaiting(false);
      return;
    }

    setIsWaiting(true);
    setRetryCount(prev => {
      const next = prev + 1;
      if (next <= MAX_AUTO_RETRIES) {
        retryTimerRef.current = setTimeout(() => {
          setIsWaiting(false);
          setPlayerKey(k => k + 1);
        }, RETRY_INTERVAL_MS);
      }
      return next;
    });
  }, []);

  // ライブ中は短命の視聴トークン付き HLS URL を取得（リトライ時は再取得）
  useEffect(() => {
    if (!id || stream?.status !== 'live') {
      setHlsUrl(null);
      return;
    }
    let cancelled = false;
    createHlsSession(id)
      .then(({ hls_url }) => {
        if (!cancelled) setHlsUrl(`${BACKEND_URL}${hls_url}`);
      })
      .catch(() => {
        if (!cancelled) handlePlayerError();
      });
    return () => {
      cancelled = true;
    };
  }, [id, stream?.status, playerKey, handlePlayerError]);

  // ストリームが live でなくなったらリトライ状態をリセット（セッション通算もリセット）
  useEffect(() => {
    if (stream?.status !== 'live') {
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      setRetryCount(0);
      setIsWaiting(false);
      sessionRecoveriesRef.current = 0;
      setSessionGaveUp(false);
    }
  }, [stream?.status]);

  useEffect(() => () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, []);

  if (loading) return <div className={styles['live-stream-detail-page']}>読み込み中...</div>;
  if (error || !stream) {
    return (
      <div className={styles['live-stream-detail-page']}>
        <p>{error ?? 'Not found'}</p>
        <button onClick={fetchStream} className={styles['live-stream-detail-page__retry']}>
          再試行
        </button>
      </div>
    );
  }

  const isLive = stream.status === 'live';
  const gaveUp = retryCount > MAX_AUTO_RETRIES || sessionGaveUp;

  return (
    <div className={styles['live-stream-detail-page']}>
      <Link to="/live-streams" className={styles['live-stream-detail-page__back']}>
        ← ライブ配信一覧
      </Link>

      <h1 className={styles['live-stream-detail-page__title']}>{stream.title}</h1>

      <span
        className={`${styles['live-stream-detail-page__status']} ${styles[`live-stream-detail-page__status--${stream.status}`]}`}
      >
        {STATUS_LABELS[stream.status]}
      </span>

      {isLive ? (
        <div className={styles['live-stream-detail-page__player']}>
          {gaveUp ? (
            <div className={styles['live-stream-detail-page__waiting']}>
              <p>映像を受信できませんでした。配信機器の接続を確認してください。</p>
              <button
                onClick={() => {
                  setRetryCount(0); setIsWaiting(false);
                  sessionRecoveriesRef.current = 0; setSessionGaveUp(false);
                  setPlayerKey(k => k + 1);
                }}
                className={styles['live-stream-detail-page__retry']}
              >
                再試行
              </button>
            </div>
          ) : (
            <div className={styles['live-stream-detail-page__player-wrap']}>
              {(isWaiting || !hlsUrl) && (
                <div className={styles['live-stream-detail-page__connecting']}>
                  配信映像を受信しています... ({retryCount}/{MAX_AUTO_RETRIES})
                </div>
              )}
              {hlsUrl && (
                <VideoJSPlayer
                  key={playerKey}
                  videoId={`${stream.id}-${playerKey}`}
                  videoUrl={hlsUrl}
                  autoPlay
                  muted={false}
                  liveMode
                  useStreaming={false}
                  onPlaybackStarted={() => {
                    // 再生に成功したら連続失敗カウンタと保留中の復帰タイマーを解除する。
                    // タイマーを残すと、後から発火して正常再生を remount してしまう。
                    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
                    setIsWaiting(false); setRetryCount(0);
                  }}
                  onError={handlePlayerError}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={styles['live-stream-detail-page__offline']}>
          {stream.status === 'scheduled' ? '配信開始をお待ちください' : '配信は終了しました'}
        </div>
      )}

      {stream.description && (
        <p className={styles['live-stream-detail-page__description']}>{stream.description}</p>
      )}

      {stream.archive_video_id && stream.archive_video_accessible && (
        <Link
          to={`/videos/${stream.archive_video_id}`}
          className={styles['live-stream-detail-page__archive-link']}
        >
          録画アーカイブを視聴する →
        </Link>
      )}
    </div>
  );
}
